/**
 * "Drive by..." — chips of well-rated places sitting along the selected
 * route. Tapping one re-runs route generation with that place added as a
 * waypoint, so the new route physically passes by it.
 *
 * Pulls from Google Places Nearby (via our edge function) using the route
 * polyline as sample points. Cached for 1h per route by the underlying API.
 */

import { useEffect, useMemo, useRef, useState } from "react";
import { Loader2, MapPin, Trees, Landmark, Building2, BookOpen, Coffee, ShoppingBag, X, Sparkles, Croissant, Scroll, Mountain, Plus, Check, Fuel, Bath, Car, Footprints, Bike, ThumbsUp, ThumbsDown } from "lucide-react";
import { searchPlacesAlongRoute, type GooglePlaceHit, type GooglePlaceType } from "@/api/googlePlaces";
import {
  fetchStopsAlongRoute, voteStop, voteOsmPlace, buriedByConsensus,
  type NominatedStop,
} from "@/api/nominatedStops";
import { spreadAlongTrip } from "@/utils/detourSpread";
import { toast } from "sonner";
import type { LngLat } from "@/utils/polyline";
import { haversine, nearestPointOnPolyline } from "@/utils/polyline";
import { fmtDistance } from "@/utils/units";
import type { TravelMode } from "@/api/routing";
import { fetchAllModeEtaMatrix, type AllModeEtas } from "@/api/etaMatrix";
import {
  BEAUTIFUL_WALKING_STREETS,
  QUIRKY_WALKING_LANDMARKS,
  HISTORIC_WALKING_LANDMARKS,
  FAMOUS_BAKERIES,
} from "@/api/pleasantWaypoints";
import { EPIC_ROAD_TRIP_STOPS } from "@/api/epicStops";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

/** Curated category — drives icon, "more like this" filter, and scoring. */
type CuratedCat = "must-see" | "quirky" | "scenic" | "historic" | "bakery";
/** A filter category — either a curated catalog category or a Google place
 *  type. Lets walking/biking trips show "Parks", "Cafés", "Landmarks" pills
 *  even when no curated catalog entries are nearby. */
type FilterCat = CuratedCat | GooglePlaceType;

interface Props {
  /** Coordinates of the currently-selected route polyline. */
  routeCoords: LngLat[];
  /** Stable cache/render key — change when the route changes. */
  routeKey: string;
  travelMode: TravelMode;
  /** All currently-applied drive-by waypoints — each chip stays green so the
   *  user sees what they've already added. */
  activeWaypointIds: string[];
  /** Tap a chip → host adds (or removes if already active) it as a stop. */
  onPick: (place: GooglePlaceHit) => void;
  /** Add many places at once (used by "Add all"). */
  onPickMany?: (places: GooglePlaceHit[]) => void;
  /** Clear all drive-by stops at once. */
  onClear: () => void;
  /** When set (typically the user's live GPS during navigation), the chip
   *  list is ordered by straight-line distance from this point — closest
   *  first — instead of the bucket-spread along the whole route. */
  sortFromPoint?: LngLat;
}

const ICON_FOR: Record<GooglePlaceType, typeof Trees> = {
  park: Trees,
  tourist_attraction: Landmark,
  museum: Building2,
  church: Landmark,
  library: BookOpen,
  cafe: Coffee,
  shop: ShoppingBag,
  restaurant: Coffee,
  gas: Fuel,
  bathroom: Bath,
};

const LABEL_FOR: Record<GooglePlaceType, string> = {
  park: "park",
  tourist_attraction: "landmark",
  museum: "museum",
  church: "church",
  library: "library",
  cafe: "café",
  shop: "shop",
  restaurant: "restaurant",
  gas: "gas",
  bathroom: "bathroom",
};

/** Drop categories that don't make sense for the current travel mode. */
function typesForMode(mode: TravelMode): GooglePlaceType[] {
  if (mode === "driving") {
    // Drivers want destinations worth a *detour* — scenic spots, landmarks,
    // parks. Skip café / shop noise (those are walking-pace stops).
    // GAS is always available (Morgan: "gas definitely needs to be one of
    // the stops that you can choose") — a tank is a real need, not noise.
    return ["park", "tourist_attraction", "museum", "church", "library", "gas"];
  }
  // Walkers and cyclists enjoy quick stops — keep the full menu.
  return ["park", "tourist_attraction", "museum", "church", "library", "cafe", "shop"];
}

/** Per-mode detour cost ADDED TO THE ROUTE by inserting `poi` as a waypoint.
 *  Approximated as a round-trip from the route's nearest point to the POI:
 *      added distance ≈ 2 × leg(snap → poi)
 *      added time     ≈ added distance / typical mode speed
 *  This is a deliberate approximation (not a re-route) so it stays cheap
 *  and consistent across every candidate. The numbers are honest about
 *  what an out-and-back side trip from the existing corridor will cost. */
type DetourCost = { addedDistanceM: number; addedDurationS: number };
type DetourByMode = Record<TravelMode, DetourCost>;
const MODE_SPEED_MPS: Record<TravelMode, number> = {
  driving: 13.4, // ~30 mph average on mixed roads
  biking:  4.5,  // ~10 mph
  walking: 1.35, // ~3 mph
};
// Exported (Aug 6): the CarPlay bridge stamps "+min · +mi" onto mid-drive
// category results (Morgan: "for coffee gas and groceries we want to know
// how many extra minutes/miles going would add").
export function computeRouteDetour(routeCoords: LngLat[], poi: LngLat): DetourByMode | null {
  if (routeCoords.length < 2) return null;
  const snap = nearestPointOnPolyline(poi, routeCoords);
  // Round-trip side trip from the route to the POI and back.
  const addedM = Math.max(0, snap.distance) * 2;
  const out: DetourByMode = {
    driving: { addedDistanceM: addedM, addedDurationS: addedM / MODE_SPEED_MPS.driving },
    biking:  { addedDistanceM: addedM, addedDurationS: addedM / MODE_SPEED_MPS.biking },
    walking: { addedDistanceM: addedM, addedDurationS: addedM / MODE_SPEED_MPS.walking },
  };
  return out;
}

export function DriveByOptions({
  routeCoords,
  routeKey,
  travelMode,
  activeWaypointIds,
  onPick,
  onPickMany,
  onClear,
  sortFromPoint,
}: Props) {
  const [hits, setHits] = useState<GooglePlaceHit[]>([]);

  // "WORTH A STOP" — the community layer (Morgan, Aug 4). Nominated stops
  // and vote tallies for everything the panel shows. Votes are the quality
  // signal that Google's star ratings used to provide.
  const [stops, setStops] = useState<NominatedStop[]>([]);
  useEffect(() => {
    if (routeCoords.length < 2) return;
    let cancelled = false;
    void fetchStopsAlongRoute(routeCoords)
      .then((s) => { if (!cancelled) setStops(s); })
      .catch(() => { /* community layer is an enhancement */ });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeKey]);

  /** The community record behind a panel hit, if any. */
  const stopForHit = (h: GooglePlaceHit): NominatedStop | undefined =>
    h.id.startsWith("stop:")
      ? stops.find((s) => `stop:${s.id}` === h.id)
      : stops.find((s) => s.osmRef === h.id);

  /** Cast/toggle a vote. Optimistic; reverts if the server refuses. */
  const handleVote = async (h: GooglePlaceHit, v: 1 | -1) => {
    const existing = stopForHit(h);
    const next: 1 | -1 | 0 = existing && existing.myVote === v ? 0 : v;
    const before = stops;
    setStops((prev) => {
      if (existing) {
        return prev.map((s) => s.id !== existing.id ? s : {
          ...s,
          up: s.up + (next === 1 ? 1 : 0) - (s.myVote === 1 ? 1 : 0),
          down: s.down + (next === -1 ? 1 : 0) - (s.myVote === -1 ? 1 : 0),
          myVote: next,
        });
      }
      // First vote on an OSM place — promote it locally; server id patched in.
      return [...prev, {
        id: `pending-${h.id}`, name: h.name, why: null,
        lat: h.center[1], lng: h.center[0], source: "osm", osmRef: h.id,
        up: v === 1 ? 1 : 0, down: v === -1 ? 1 : 0, myVote: v,
      }];
    });
    try {
      if (existing && !existing.id.startsWith("pending-")) {
        await voteStop(existing.id, next);
      } else if (!existing) {
        const { stopId } = await voteOsmPlace({ id: h.id, name: h.name, center: h.center }, v);
        if (stopId) setStops((prev) => prev.map((s) => s.osmRef === h.id ? { ...s, id: stopId } : s));
        else setStops(before); // insert refused (e.g. signed out) — be honest
      }
    } catch {
      setStops(before);
    }
  };

  // User-nominated stops surface as first-class picks ("Local pick").
  // Consensus burial applies to everything: ≥3 downvotes and 2:1 against.
  const localPickHits = useMemo<GooglePlaceHit[]>(() =>
    stops
      .filter((s) => s.source === "user" && !s.id.startsWith("pending-") && !buriedByConsensus(s.up, s.down))
      .map((s) => ({
        id: `stop:${s.id}`,
        name: s.name,
        type: "tourist_attraction" as GooglePlaceType,
        weight: 1.6,
        rating: 0,
        userRatingCount: 0,
        center: [s.lng, s.lat] as LngLat,
        blurb: s.why ?? undefined,
      })), [stops]);
  const buriedOsmRefs = useMemo(() =>
    new Set(stops.filter((s) => s.osmRef && buriedByConsensus(s.up, s.down)).map((s) => s.osmRef as string)),
    [stops]);
  const [loading, setLoading] = useState(false);
  /** Expanded mode shows many more chips per category and widens curated radius. */
  const [expanded, setExpanded] = useState(false);

  // Total route length (km) — drives how strict we get about "is this
  // place really worth a detour?" The longer the trip, the higher the bar.
  const routeKm = useMemo(() => {
    let m = 0;
    for (let i = 1; i < routeCoords.length; i++) {
      m += haversine(routeCoords[i - 1], routeCoords[i]);
    }
    return m / 1000;
  }, [routeCoords]);

  useEffect(() => {
    if (routeCoords.length < 2) {
      setHits([]);
      return;
    }
    let cancelled = false;
    setLoading(true);

    // Trip-length tiers. Short hops surface neighborhood-scale picks.
    // Long road trips should only surface places that are genuinely famous
    // / well-reviewed — the kind of stop you'd remember telling friends
    // about, not "a decent café 4 miles in."
    const isDriving = travelMode === "driving";
    const long = isDriving && routeKm >= 80;        // ~50+ mi
    const epic = isDriving && routeKm >= 200;       // ~125+ mi
    // Thresholds loosened (Morgan: "more detours along the route") so more
    // than a couple of famous parks survive on longer drives — while still
    // keeping the bar high enough that only genuinely worthwhile stops show.
    const minReviews = !isDriving
      ? 5
      : epic ? 400
      : long ? 100
      : 15;
    const minRating = !isDriving
      ? 3.8
      : epic ? 4.4
      : long ? 4.1
      : 3.9;

    void (async () => {
      try {
        const results = await searchPlacesAlongRoute(routeCoords, {
          types: typesForMode(travelMode),
          // Tighter radius than scoring's 250m — we want places truly on the
          // way, not just "in the neighborhood". Walkers/cyclists get a
          // bigger radius + denser sampling so even short trips surface
          // nearby cafés, parks and shops worth stopping at. Long drives
          // get a wider radius so a famous landmark a few minutes off the
          // exit still qualifies.
          // Cast a wide net so every trip surfaces real recommendations
          // — up to ~2 miles off-route. Closer hits get prioritized at
          // ranking time so nearby stops still appear first.
          radiusM: isDriving ? (epic ? 3200 : long ? 2400 : 1600) : (travelMode === "biking" ? 2400 : 1400),
          sampleSpacingM: travelMode === "walking" ? 80 : travelMode === "biking" ? 120 : 150,
          // Drivers cover long distances — bump the sample cap so the middle
          // of the route gets surveyed, not just the endpoints.
          maxSamples: isDriving ? 40 : 30,
          minReviews,
          minRating,
        });
        if (!cancelled) setHits(results);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeKey, travelMode, routeKm]);

  // For walking & biking trips, inject our curated quirky landmarks
  // (Dakota, Friends apartment, Vanderbilt-era mansions, Abbey Road, etc.)
  // as tappable chips when they sit near the actual route polyline. Google
  // Places misses these story-worthy stops because they're addresses, not
  // POIs. Biking gets a wider radius since you cover more ground.
  const curatedHits = useMemo<GooglePlaceHit[]>(() => {
    if (routeCoords.length < 2) return [];
    // Expanded mode widens the search so distant curated gems show up
    // (e.g. scenic streets a few blocks off the route). Drivers cover much
    // more ground, so curated landmarks within a few km of the route still
    // count as "on the way".
    const baseMax = travelMode === "driving" ? 3000 : travelMode === "biking" ? 900 : 500;
    const maxDistM = expanded ? baseMax * 4 : baseMax;
    const sources: { items: typeof QUIRKY_WALKING_LANDMARKS; cat: CuratedCat }[] = [
      { items: QUIRKY_WALKING_LANDMARKS, cat: "quirky" },
      { items: BEAUTIFUL_WALKING_STREETS, cat: "scenic" },
      { items: HISTORIC_WALKING_LANDMARKS, cat: "historic" },
      { items: FAMOUS_BAKERIES, cat: "bakery" },
    ];
    const out: GooglePlaceHit[] = [];
    for (const { items, cat } of sources) {
      for (const c of items) {
        let best = Infinity;
        for (const v of routeCoords) {
          const d = haversine(v, c.coord);
          if (d < best) best = d;
          if (best < 50) break;
        }
        if (best > maxDistM) continue;
        out.push({
          id: `curated:${cat}:${c.name}`,
          name: c.name,
          type: "tourist_attraction",
          weight: 1.8,
          rating: 4.8,
          userRatingCount: 5000,
          center: c.coord,
        });
      }
    }

    // Long road trips (~80km+ driving): inject the curated "must-see" list of
    // national parks, iconic landmarks, and roadside quirks (World's Largest
    // Cheese Ball energy). We use a much wider radius — these are stops
    // people happily detour 30+ minutes for.
    const isDriving = travelMode === "driving";
    const longTrip = isDriving && routeKm >= 80;
    if (longTrip) {
      // Cap detour radius by both an absolute ceiling AND a fraction of the
      // total trip length. A ~80km trip should NOT surface a stop 40km off
      // route — that's a 100km round-trip detour, longer than the trip
      // itself. Rule of thumb: out-and-back detour shouldn't exceed ~15%
      // of trip length (and never more than the absolute ceiling).
      const absCeil = expanded ? 50_000 : 25_000; // ~15mi / ~30mi
      const tripFrac = (routeKm * 1000) * (expanded ? 0.20 : 0.10) / 2;
      const epicMaxM = Math.min(absCeil, tripFrac);
      for (const stop of EPIC_ROAD_TRIP_STOPS) {
        let best = Infinity;
        for (const v of routeCoords) {
          const d = haversine(v, stop.coord);
          if (d < best) best = d;
          if (best < 200) break;
        }
        if (best > epicMaxM) continue;
        out.push({
          id: `curated:must-see:${stop.name}`,
          name: stop.name,
          type: "tourist_attraction",
          // Heavy weight so these float to the top — they're the
          // bucket-list reasons to take a road trip.
          weight: 3.0,
          rating: 4.9,
          userRatingCount: 20000,
          center: stop.coord,
          blurb: stop.blurb,
        });
      }
    }
    return out;
  }, [travelMode, routeCoords, routeKm, expanded]);

  /** Extract curated category from a chip id, or null for Google hits. */
  const catOf = (id: string): CuratedCat | null => {
    if (!id.startsWith("curated:")) return null;
    return id.split(":")[1] as CuratedCat;
  };

  /** Active "more like this" filter — when set, only chips of this category
   *  are shown. Cleared when the user picks a different chip or clears their
   *  detour. */
  const [filterCat, setFilterCat] = useState<FilterCat | null>(null);

  const verb = travelMode === "driving" ? "Drive by" : travelMode === "biking" ? "Ride past" : "Walk past";

  /** Per-category icon + label for filter pills and chip rendering. Covers
   *  both curated catalog categories AND Google place types so walking/
   *  biking trips show useful pills (Parks, Cafés, Landmarks…) even where
   *  the curated catalogs are sparse. */
  const CAT_META: Record<FilterCat, { icon: typeof Sparkles; label: string }> = {
    "must-see":         { icon: Mountain,    label: "Worth a detour" },
    quirky:             { icon: Sparkles,    label: "Roadside quirks" },
    scenic:             { icon: Trees,       label: "Scenic streets" },
    historic:           { icon: Scroll,      label: "Historic" },
    bakery:             { icon: Croissant,   label: "Bakeries" },
    park:               { icon: Trees,       label: "Parks" },
    tourist_attraction: { icon: Landmark,    label: "Landmarks" },
    museum:             { icon: Building2,   label: "Museums" },
    church:             { icon: Landmark,    label: "Churches" },
    library:            { icon: BookOpen,    label: "Libraries" },
    cafe:               { icon: Coffee,      label: "Cafés" },
    shop:               { icon: ShoppingBag, label: "Shops" },
    restaurant:         { icon: Coffee,      label: "Restaurants" },
    gas:                { icon: Fuel,        label: "Gas" },
    bathroom:           { icon: Bath,        label: "Bathrooms" },
  };

  /** Resolve a chip's category — curated id or Google place type. */
  const filterCatOf = (h: GooglePlaceHit): FilterCat | null => {
    const c = catOf(h.id);
    if (c) return c;
    return h.type ?? null;
  };

  /** Which categories actually have at least one nearby hit that would
   *  survive the same end-of-route trim used for the visible chips. We
   *  recompute progress here so a "Libraries" pill never appears unless a
   *  library would really show up when selected. */
  const availableCats = useMemo<FilterCat[]>(() => {
    const isDriving = travelMode === "driving";
    const long = isDriving && routeKm >= 80;
    // Distance-scaled arrival buffer (see the matching note below) — the
    // category counters must agree with the visible list.
    const endCutoff = Math.max(long ? 0.92 : 0.88, 1 - 2.5 / Math.max(routeKm, 1));

    // Cumulative-distance lookup for progress-of-point.
    const cum: number[] = [0];
    for (let i = 1; i < routeCoords.length; i++) {
      cum.push(cum[i - 1] + haversine(routeCoords[i - 1], routeCoords[i]));
    }
    const totalLen = cum[cum.length - 1] || 1;
    const progressOf = (pt: LngLat): number => {
      let bestI = 0;
      let bestD = Infinity;
      for (let i = 0; i < routeCoords.length; i++) {
        const d = haversine(routeCoords[i], pt);
        if (d < bestD) { bestD = d; bestI = i; }
      }
      return cum[bestI] / totalLen;
    };

    const counts = new Map<FilterCat, number>();
    const bump = (c: FilterCat | null, h: GooglePlaceHit) => {
      if (!c) return;
      if (progressOf(h.center) > endCutoff) return;
      counts.set(c, (counts.get(c) ?? 0) + 1);
    };
    for (const h of curatedHits) bump(catOf(h.id), h);
    for (const h of hits) bump(h.type ?? null, h);

    const order: FilterCat[] = [
      "must-see", "quirky", "scenic", "historic", "bakery",
      "park", "tourist_attraction", "museum", "church", "library", "cafe", "shop", "restaurant",
    ];
    return order.filter((c) => (counts.get(c) ?? 0) > 0);
  }, [curatedHits, hits, routeCoords, routeKm, travelMode]);

  // Top picks — sort by per-type weight × log(reviews)^fame × rating, then
  // spread the selection along the route so chips aren't clustered near
  // the start or destination.
  const top = useMemo(() => {
    const matchesFilter = (h: GooglePlaceHit): boolean => {
      if (!filterCat) return true;
      const c = filterCatOf(h);
      return c === filterCat;
    };
    const pool = [...curatedHits, ...localPickHits, ...hits.filter((h) => !buriedOsmRefs.has(h.id))];
    const filtered = filterCat ? pool.filter(matchesFilter) : pool;
    if (filtered.length === 0) return [];

    // Build a cumulative-distance array for the route once.
    const cum: number[] = [0];
    for (let i = 1; i < routeCoords.length; i++) {
      cum.push(cum[i - 1] + haversine(routeCoords[i - 1], routeCoords[i]));
    }
    const totalLen = cum[cum.length - 1] || 1;

    /** Approximate progress (0..1) of a point along the route by nearest vertex. */
    const progressOf = (pt: LngLat): number => {
      let bestI = 0;
      let bestD = Infinity;
      for (let i = 0; i < routeCoords.length; i++) {
        const d = haversine(routeCoords[i], pt);
        if (d < bestD) { bestD = d; bestI = i; }
      }
      return cum[bestI] / totalLen;
    };

    const isDriving = travelMode === "driving";
    const long = isDriving && routeKm >= 80;

    /** Distance (meters) of a point from the nearest route vertex. */
    const distToRouteM = (pt: LngLat): number => {
      let best = Infinity;
      for (const v of routeCoords) {
        const d = haversine(v, pt);
        if (d < best) best = d;
        if (best < 30) break;
      }
      return best;
    };

    const scored = filtered.map((h) => {
      const isCurated = h.id.startsWith("curated:");
      const isLocalPick = h.id.startsWith("stop:");
      // COMMUNITY VOTES replace Google's rating×reviews terms (Aug 4:
      // "I don't even use ratings"). Net thumbs scale the score — an
      // upvoted café outranks its silent neighbors; downvotes sink it
      // well before consensus burial removes it entirely.
      const s = stopForHit(h);
      const net = s ? s.up - s.down : 0;
      const voteBoost = Math.min(3, Math.max(0.4, 1 + 0.35 * net));
      // Proximity boost: stops on/near the route win over 2-mile detours,
      // unless they're truly famous (curated must-sees keep their edge).
      const distM = distToRouteM(h.center);
      const proxBoost = isCurated ? 1 : 1 / (1 + distM / 600); // ~0.5 at 600m, ~0.23 at 2km
      return {
        h,
        score: h.weight * (isCurated ? 2.5 : isLocalPick ? 2.2 : 1) * proxBoost * voteBoost,
        progress: progressOf(h.center),
      };
    });
    scored.sort((a, b) => b.score - a.score);

    // Don't surface picks that sit basically AT the destination — they
    // create a visual cluster of stars on top of the end pin and aren't
    // useful "on-the-way" stops. Reserve DISTANCE, not percentage (the
    // T+L Windham trip: a 12% cutoff banned the last 17 MILES — Cairo,
    // East Windham, the Five State Lookout all excluded, which is why
    // "all of the detours are close to the starting point"). ~2.5 km of
    // arrival buffer, floored at the old behavior for short errands.
    const endCutoff = Math.max(long ? 0.92 : 0.88, 1 - 2.5 / Math.max(routeKm, 1));
    const seen = new Set<string>();
    const unique: typeof scored = [];
    for (const s of scored) {
      if (s.progress > endCutoff) continue;
      const key = s.h.name.toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      unique.push(s);
    }

    // In navigation: order strictly by distance from the user's current
    // position so the nearest detours sit at the top of the list.
    if (sortFromPoint) {
      const cap = expanded ? 80 : 24;
      return unique
        .slice()
        .sort((a, b) => haversine(sortFromPoint, a.h.center) - haversine(sortFromPoint, b.h.center))
        .slice(0, cap)
        .map((s) => s.h);
    }

    // When a category filter is on, skip the bucket-spread step — the user
    // asked for "more like this," so just show the best ones regardless of
    // where they sit along the trip.
    if (filterCat) {
      return unique.slice(0, expanded ? 60 : 12).map((s) => s.h);
    }

    // Spread selection extracted to detourSpread.ts (pure, daily-audited —
    // Morgan: "how do i know if detours are along the entire route and not
    // clustered in the starting point"). Behavior unchanged.
    return spreadAlongTrip(unique, {
      bucketCount: long ? 6 : 4,
      perBucket: expanded ? (long ? 8 : 14) : (long ? 5 : 9),
      max: expanded ? 80 : (long ? 24 : 28),
      // Minimum geographic separation between picks. Without this, the
      // chips can all land within a ~300m blob (e.g. clustered in Central
      // Park). Scaled to trip length.
      minSepM: Math.max(60, Math.min(1000, routeKm * 1000 * 0.025)),
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hits, curatedHits, localPickHits, buriedOsmRefs, stops, routeCoords, routeKm, travelMode, filterCat, expanded, sortFromPoint]);

  // Build the visible chip list: always include all currently-active picks
  // (so they stay green and visible even if a category filter would hide
  // them), then fill the rest with `top`. The pick stays in the rail so the
  // user can tap it again to remove it.
  const allHitsById = new Map<string, GooglePlaceHit>();
  for (const h of [...curatedHits, ...localPickHits, ...hits]) allHitsById.set(h.id, h);
  const visible: GooglePlaceHit[] = [];
  const seenIds = new Set<string>();
  for (const id of activeWaypointIds) {
    const h = allHitsById.get(id);
    if (h && !seenIds.has(h.id)) {
      visible.push(h);
      seenIds.add(h.id);
    }
  }
  for (const h of top) {
    if (!seenIds.has(h.id)) {
      visible.push(h);
      seenIds.add(h.id);
    }
  }

  // Real-network ETAs from the user's current position to each visible
  // detour, via Mapbox Matrix API. Refetches when the user moves more than
  // ~150m or when the visible candidate set changes. Falls back to
  // straight-line estimates while loading or on failure.
  const [etaMap, setEtaMap] = useState<Map<string, AllModeEtas>>(new Map());
  const fromKey = sortFromPoint
    ? `${sortFromPoint[0].toFixed(3)},${sortFromPoint[1].toFixed(3)}`
    : "";
  const visibleIdsKey = visible.map((h) => h.id).join("|");
  useEffect(() => {
    if (!sortFromPoint || visible.length === 0) return;
    let cancelled = false;
    const targets = visible.map((h) => h.center);
    const ids = visible.map((h) => h.id);
    (async () => {
      const all = await fetchAllModeEtaMatrix(sortFromPoint, targets);
      if (cancelled) return;
      setEtaMap((prev) => {
        const next = new Map(prev);
        for (let i = 0; i < ids.length; i++) {
          if (Object.keys(all[i]).length > 0) {
            next.set(`${fromKey}::${ids[i]}`, all[i]);
          }
        }
        return next;
      });
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromKey, visibleIdsKey]);

  const orderedVisible = sortFromPoint
    ? visible.slice().sort((a, b) => {
        const aEta = etaMap.get(`${fromKey}::${a.id}`)?.[travelMode]?.distanceM;
        const bEta = etaMap.get(`${fromKey}::${b.id}`)?.[travelMode]?.distanceM;
        const aDist = aEta ?? haversine(sortFromPoint, a.center);
        const bDist = bEta ?? haversine(sortFromPoint, b.center);
        return aDist - bDist;
      })
    : visible;

  const isEmpty = !loading && top.length === 0 && activeWaypointIds.length === 0 && availableCats.length === 0;
  // In navigation (sortFromPoint) an empty box clutters the rail — hide it.
  // On the route screen, the user explicitly tapped "Detours", so show a
  // clear "nothing found" message instead of silently rendering nothing
  // (which reads as "the button is broken"). (Morgan)
  if (isEmpty && sortFromPoint) return null;
  if (isEmpty) {
    return (
      <div className="rounded-2xl border border-border bg-card/60 p-3">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground flex items-center gap-1.5 mb-1">
          <MapPin className="w-3.5 h-3.5 text-pleasant" />
          Add a stop
        </h3>
        <p className="text-xs text-muted-foreground">
          No suggested stops along this route yet — add your own with the + next to your destination.
        </p>
      </div>
    );
  }


  return (
    <div className="rounded-2xl border border-border bg-card/60 p-3">
      <div className="flex items-center justify-between mb-2">
        {/* "Detours" became "Add a stop" (Morgan, Aug 5) — same engine,
            honest verb: these chips add stops to YOUR route, and starring
            one recommends it to the community. */}
        <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground flex items-center gap-1.5">
          <MapPin className="w-3.5 h-3.5 text-pleasant" />
          Add a stop
        </h3>
        <div className="flex items-center gap-2">
          {loading && <Loader2 className="w-3.5 h-3.5 animate-spin text-muted-foreground" />}
          {activeWaypointIds.length > 0 && (
            <button
              type="button"
              onClick={onClear}
              className="text-[11px] text-muted-foreground hover:text-foreground underline-offset-2 hover:underline"
            >
              Clear all
            </button>
          )}
        </div>
      </div>

      {/* Category filter pills — let the user say "more like this" by
       *  narrowing the chip list to a single curated theme (historic,
       *  bakeries, quirky landmarks, scenic streets). Only categories with
       *  at least one nearby hit are shown. */}
      {availableCats.length > 0 && (
        <div className="flex flex-wrap gap-1 mb-2">
          <button
            type="button"
            onClick={() => setFilterCat(null)}
            className={
              "h-7 px-2.5 rounded-full text-[11px] font-medium border transition-smooth " +
              (filterCat === null
                ? "bg-foreground text-background border-foreground"
                : "bg-transparent border-muted-foreground/20 text-muted-foreground hover:text-foreground hover:border-muted-foreground/40")
            }
          >
            All
          </button>
          {availableCats.map((c) => {
            const Icon = CAT_META[c].icon;
            const active = filterCat === c;
            return (
              <button
                key={c}
                type="button"
                onClick={() => setFilterCat(active ? null : c)}
                className={
                  "h-7 px-2.5 rounded-full text-[11px] font-medium border flex items-center gap-1 transition-smooth " +
                  (active
                    ? "bg-foreground text-background border-foreground"
                    : "bg-transparent border-muted-foreground/20 text-muted-foreground hover:text-foreground hover:border-muted-foreground/40")
                }
              >
                <Icon className="w-3 h-3" />
                {CAT_META[c].label}
              </button>
            );
          })}
        </div>
      )}

      {visible.length === 0 && loading && (
        <p className="text-xs text-muted-foreground py-1">Looking for spots along the route…</p>
      )}

      <div className={sortFromPoint ? "flex flex-col gap-1.5" : "flex flex-wrap gap-1.5"}>
        {orderedVisible.map((h) => {
          const cat = catOf(h.id);
          const isCurated = cat !== null;
          const isLocal = h.id.startsWith("stop:");
          const Icon = isCurated ? CAT_META[cat].icon : isLocal ? Sparkles : ICON_FOR[h.type];
          const isActive = activeWaypointIds.includes(h.id);
          const catLabel = isCurated ? CAT_META[cat!].label : isLocal ? "local pick" : LABEL_FOR[h.type];
          // Real road-network ETAs from Mapbox Matrix for ALL three travel
          // modes; the chip renders one badge per mode and highlights the
          // currently-selected one. Falls back to straight-line distance
          // (selected mode only) while the matrix is in flight.
          const modeEtas = sortFromPoint ? etaMap.get(`${fromKey}::${h.id}`) : undefined;
          const fallbackDistM = sortFromPoint && !modeEtas ? haversine(sortFromPoint, h.center) : null;
          // Detour cost added to the route by inserting this POI as a stop.
          // This is what the chip actually displays — it's the answer to
          // "how much longer does my trip get if I include this?".
          const detour = computeRouteDetour(routeCoords, h.center);
          const stopRec = stopForHit(h);
          return (
            <StopChip
              key={h.id}
              hit={h}
              icon={Icon}
              isActive={isActive}
              isCurated={isCurated}
              isLocalPick={h.id.startsWith("stop:")}
              votes={stopRec ? { up: stopRec.up, down: stopRec.down, mine: stopRec.myVote } : { up: 0, down: 0, mine: 0 }}
              onVote={isCurated ? undefined : (v) => void handleVote(h, v)}
              catLabel={catLabel}
              modeEtas={modeEtas}
              detourFromRoute={detour}
              fallbackDistanceM={fallbackDistM}
              currentMode={travelMode}
              fullWidth={!!sortFromPoint}
              onToggle={() => {
                // Picking a detour used to auto-narrow the list to that chip's
                // category ("more like this"), which collapsed the rail to a
                // single option. Morgan wants to keep browsing the full set of
                // offered detours after adding one, so just add/remove it and
                // leave the filter alone (the filter pills still work manually).
                const adding = !isActive;
                onPick(h);
                // STAR AT THE MOMENT OF ADDING (Morgan, Aug 5: "as you add
                // stop you have an option to check a star that says recommend
                // stop to pleasant community"). The star is the same signal
                // as a thumbs-up — it promotes the place into nominated_stops
                // as a Local pick. Curated stops don't need recommending.
                if (adding && !isCurated) {
                  toast("Added to your route", {
                    description: "Star it and it becomes a Local pick for the Pleasant community",
                    action: { label: "★ Recommend", onClick: () => void handleVote(h, 1) },
                    duration: 6000,
                  });
                }
              }}
            />
          );
        })}
        {/* "More" toggle — always visible when filtering a category, or
         *  when there are more candidates available than currently shown. */}
        {(filterCat !== null || expanded || (curatedHits.length + hits.length) > top.length) && (
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="h-9 px-3 rounded-full border border-dashed border-muted-foreground/30 text-xs font-medium text-muted-foreground hover:text-foreground hover:border-muted-foreground/60 transition-smooth"
          >
            {expanded ? "Show fewer" : "Show more"}
          </button>
        )}
      </div>
    </div>
  );
}

/**
 * Single chip + popover. Tapping the chip OPENS an explainer bubble (it
 * does not immediately add the stop) so users can see *why* a place is
 * worth visiting first. The popover has Add/Remove and "More like this"
 * actions. The blurb is fetched lazily from `poi-fact` on first open and
 * cached on the chip's lifetime.
 */
function StopChip({
  hit,
  icon: Icon,
  isActive,
  isCurated,
  isLocalPick,
  votes,
  onVote,
  catLabel,
  onToggle,
  modeEtas,
  detourFromRoute,
  fallbackDistanceM,
  currentMode,
  fullWidth,
}: {
  hit: GooglePlaceHit;
  icon: typeof Sparkles;
  isActive: boolean;
  isCurated: boolean;
  isLocalPick?: boolean;
  /** Community verdict — thumbs replace star ratings (Morgan, Aug 4). */
  votes?: { up: number; down: number; mine: number };
  onVote?: (v: 1 | -1) => void;
  catLabel: string;
  onToggle: () => void;
  modeEtas?: AllModeEtas;
  detourFromRoute?: DetourByMode | null;
  fallbackDistanceM?: number | null;
  currentMode?: TravelMode;
  fullWidth?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [fact, setFact] = useState<string | null>(hit.blurb ?? null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const fetched = useRef(false);

  useEffect(() => {
    if (!open || fetched.current) return;
    // If we already have a curator blurb, skip the network call.
    if (hit.blurb) { fetched.current = true; return; }
    fetched.current = true;
    setLoading(true);
    (async () => {
      try {
        const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/poi-fact`;
        const resp = await fetch(url, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY}`,
          },
          body: JSON.stringify({
            name: hit.name,
            category: catLabel,
            lat: hit.center[1],
            lng: hit.center[0],
          }),
        });
        const data = await resp.json().catch(() => ({}));
        const f = typeof data?.fact === "string" ? data.fact.trim() : "";
        if (f) setFact(f);
        else setError(true);
      } catch {
        setError(true);
      } finally {
        setLoading(false);
      }
    })();
  }, [open, hit, catLabel]);

  // Build a list of {mode, icon, label} entries for the badges. The
  // currently-selected travel mode is rendered emphasised.
  const MODE_ENTRIES: Array<{ mode: TravelMode; icon: typeof Car }> = [
    { mode: "driving", icon: Car },
    { mode: "walking", icon: Footprints },
    { mode: "biking", icon: Bike },
  ];
  const selectedMode = modeEtas?.[currentMode ?? "driving"];
  const inlineDistanceM =
    selectedMode?.distanceM ?? (typeof fallbackDistanceM === "number" ? fallbackDistanceM : null);
  const inlineEtaMin = selectedMode ? Math.max(1, Math.round(selectedMode.durationS / 60)) : null;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          onClick={(e) => {
            // Tapping the chip immediately adds (or removes) the detour.
            // The explainer popover can still be opened via the info button.
            e.preventDefault();
            onToggle();
          }}
          onContextMenu={(e) => {
            // Long-press / right-click reveals the explainer popover.
            e.preventDefault();
            setOpen(true);
          }}
          className={
            (fullWidth
              ? "w-full h-auto py-2 px-3 rounded-2xl "
              : "h-9 px-3 rounded-full ") +
            "border text-xs font-medium flex items-center gap-2 transition-smooth " +
            (isActive
              ? "bg-[hsl(145_65%_42%)] text-white border-[hsl(145_65%_42%)] shadow-glow hover:bg-[hsl(145_65%_38%)]"
              : "bg-muted/60 border-muted-foreground/20 text-foreground hover:bg-muted hover:border-muted-foreground/40")
          }
          aria-pressed={isActive}
        >
          <Icon className="w-3.5 h-3.5 shrink-0" />
          <span className={"flex-1 min-w-0 flex flex-col items-start gap-0.5"}>
            <span className="flex items-center gap-1.5 w-full">
              <span className={fullWidth ? "truncate" : "truncate max-w-[160px]"}>{hit.name}</span>
              {/* Star ratings retired (Aug 4) — the community's net thumbs
                  show instead, and only once someone has actually voted. */}
              {!isCurated && votes && votes.up + votes.down > 0 && (
                <span className="opacity-70 flex items-center gap-0.5 ml-auto shrink-0">
                  <ThumbsUp className="w-3 h-3" />
                  {votes.up - votes.down > 0 ? `+${votes.up - votes.down}` : votes.up - votes.down}
                </span>
              )}
            </span>
            {fullWidth && (detourFromRoute || modeEtas) && (
              <span className="flex items-center gap-2 text-[10px] opacity-90">
                {MODE_ENTRIES.map(({ mode, icon: ModeIcon }) => {
                  // Prefer the route-detour cost (added time + distance to
                  // the trip). Only fall back to the user→POI matrix when
                  // we don't have route geometry to snap against.
                  const d = detourFromRoute?.[mode];
                  const e = modeEtas?.[mode];
                  const addedS = d?.addedDurationS ?? e?.durationS;
                  const addedM = d?.addedDistanceM ?? e?.distanceM;
                  if (addedS == null || addedM == null) return null;
                  const isSel = mode === currentMode;
                  const min = Math.max(1, Math.round(addedS / 60));
                  return (
                    <span
                      key={mode}
                      className={
                        "flex items-center gap-0.5 " +
                        (isSel ? "font-semibold opacity-100" : "opacity-60")
                      }
                      title={d ? "Approximate added time and distance for this detour" : "Estimated cost from your location"}
                    >
                      <ModeIcon className="w-2.5 h-2.5" />
                      +{min}m · +{fmtDistance(addedM)}
                    </span>
                  );
                })}
              </span>
            )}
            {fullWidth && !modeEtas && !detourFromRoute && typeof inlineDistanceM === "number" && (
              <span className="text-[10px] opacity-70">{fmtDistance(inlineDistanceM)}</span>
            )}
          </span>
          {!fullWidth && typeof inlineDistanceM === "number" && (
            <span className="opacity-70 shrink-0">
              {fmtDistance(inlineDistanceM)}
              {typeof inlineEtaMin === "number" ? ` · ${inlineEtaMin}m` : ""}
            </span>
          )}
          {isActive && <Check className="w-3 h-3 shrink-0" />}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-3" side="top" align="start">
        <div className="flex items-start gap-2 mb-2">
          <Icon className="w-4 h-4 mt-0.5 text-pleasant shrink-0" />
          <div className="min-w-0">
            <div className="text-sm font-semibold leading-tight">{hit.name}</div>
            <div className="text-[11px] text-muted-foreground mt-0.5 flex items-center gap-1.5">
              <span className="capitalize">{catLabel}</span>
              {/* Worth a stop? — the community answers with thumbs (Aug 4).
                  Tap again to remove your vote. */}
              {!isCurated && onVote && (
                <>
                  <span>·</span>
                  <span className="flex items-center gap-1">
                    <button
                      type="button"
                      aria-label="Worth a stop"
                      onClick={(e) => { e.stopPropagation(); onVote(1); }}
                      className={`flex items-center gap-0.5 px-1 py-0.5 rounded hover:bg-muted ${
                        votes?.mine === 1 ? "text-[hsl(145_65%_38%)] font-semibold" : ""
                      }`}
                    >
                      <ThumbsUp className="w-3 h-3" />{votes?.up ?? 0}
                    </button>
                    <button
                      type="button"
                      aria-label="Not worth a stop"
                      onClick={(e) => { e.stopPropagation(); onVote(-1); }}
                      className={`flex items-center gap-0.5 px-1 py-0.5 rounded hover:bg-muted ${
                        votes?.mine === -1 ? "text-destructive font-semibold" : ""
                      }`}
                    >
                      <ThumbsDown className="w-3 h-3" />{votes?.down ?? 0}
                    </button>
                  </span>
                </>
              )}
              {isLocalPick && <span className="text-pleasant font-medium">· Local pick</span>}
            </div>
          </div>
        </div>
        <div className="text-[13px] leading-snug text-foreground/90 min-h-[2.5rem]">
          {loading ? (
            <span className="flex items-center gap-1.5 text-muted-foreground">
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
              Looking up why this is worth a stop…
            </span>
          ) : fact ? (
            fact
          ) : error ? (
            <span className="text-muted-foreground italic">
              No quick blurb available — but it's a well-rated stop along your route.
            </span>
          ) : null}
        </div>
        <button
          type="button"
          onClick={() => { onToggle(); setOpen(false); }}
          className={
            "mt-3 w-full h-9 rounded-full text-xs font-semibold flex items-center justify-center gap-1.5 transition-smooth " +
            (isActive
              ? "bg-muted text-foreground hover:bg-muted/80"
              : "bg-pleasant text-white hover:bg-pleasant/90")
          }
        >
          {isActive ? (<><X className="w-3.5 h-3.5" /> Remove from route</>) : (<><Plus className="w-3.5 h-3.5" /> Add to route</>)}
        </button>
      </PopoverContent>
    </Popover>
  );
}
